/**
 * 海报「字体风格」轻量档 — 2026-07-04(反馈 #20·28)
 *
 * 现状:海报文字由图像 AI 直接画进底图(无独立文字层 / 无字体选择),此前 prompt 只有
 * 模糊「字体感觉(但不要画字)」的语义冲突措辞。轻量档做法:给用户一个「字体风格」维度,
 * 选中后把结构化字体风格词(圆润 / 手写 / 衬线 / 现代无衬线)作为软提示注入出图 prompt
 * ——图像模型据此倾向渲染对应字形。**不是**逐字精确可控字体(那是「中期档」:复活文字层
 * 真·选字体,记 backlog)。
 *
 * phrase 写作:中文具象描述 + 2-3 个英文 art/type anchor(gpt-image-2 / 万相 对英文
 * 字体术语训练密度更高)。phrase=null(auto/默认)= 不注入,由内容/画风自判。
 *
 * 注入方式对齐视觉风格 tone:FreeCreate raw 路径尾追加 `【字体风格】{phrase}`(见
 * useFreeCreateActions);跟视觉画风(styleOptions.ts)是**两个独立维度**,可各自选。
 */

export interface FontStyleOption {
  /** 稳定 key:选中态标识 + 回显(label 中文可能改,不能当 key)。 */
  key: string;
  /** UI 显示短名(2-5 字)。 */
  label: string;
  /** 注入出图 prompt 的字体风格词(中文具象 + 英文 anchor)。null = auto,不注入。 */
  phrase: string | null;
}

/** 默认档 key:自动(不注入,交给内容/画风自判)。 */
export const FONT_STYLE_AUTO = "auto";

/** 字体风格档位。auto 恒首位;四个具象档带中英双锚 phrase。 */
export const FONT_STYLES: readonly FontStyleOption[] = [
  { key: FONT_STYLE_AUTO, label: "自动", phrase: null },
  {
    key: "rounded",
    label: "圆润",
    phrase:
      "海报标题与主文字使用圆润饱满的圆角黑体，笔画柔和收圆、亲和可爱，friendly rounded sans-serif, soft rounded strokes",
  },
  {
    key: "handwritten",
    label: "手写",
    phrase:
      "海报标题与主文字使用手写风格字体（毛笔或马克笔手写体），自然随性有笔触，handwritten brush script, casual hand-lettering",
  },
  {
    key: "serif",
    label: "衬线",
    phrase:
      "海报标题与主文字使用衬线字体（宋体/明朝体风格），笔画带衬脚、优雅正式，elegant serif typeface, refined strokes with serifs",
  },
  {
    key: "sans",
    label: "现代无衬线",
    phrase:
      "海报标题与主文字使用现代简洁的无衬线黑体，几何利落、清晰易读，clean modern sans-serif, geometric, minimal",
  },
];

const _BY_KEY: ReadonlyMap<string, FontStyleOption> = new Map(FONT_STYLES.map((f) => [f.key, f]));

/** 取某档的注入 phrase;auto / 未知 key → null(不注入)。 */
export function fontPhraseForKey(key: string | null | undefined): string | null {
  if (!key) return null;
  return _BY_KEY.get(key)?.phrase ?? null;
}

/**
 * 取某档中文短名,用于详情页「本次设置」展示。auto(无实义)/ 未知 → undefined,
 * 让调用方 `|| undefined` 省略字段。
 */
export function fontLabelForKey(key: string | null | undefined): string | undefined {
  if (!key || key === FONT_STYLE_AUTO) return undefined;
  return _BY_KEY.get(key)?.label;
}
